// @flow

import React, { useState } from "react"
import { injectIntl } from "react-intl"
import { observer } from "mobx-react-lite"
import styled from "styled-components"
import { filter, includes, isUndefined, lowerCase, replace } from "lodash"
import { Timeline, Tooltip } from "antd"
import OrderSearchStore from "shared/stores/OrderSearchStore"
import WideSpinner from "shared/components/WideSpinner"
import moment from "moment"
import DotsDropdown from "@cabonline/shared/components/DotsDropdown/DotsDropdown"
import type { OrderEvent } from "shared/types/OrdersType"
import { toTitleCase } from "@cabonline/shared/utils/stringUtils"

const EVENTS_TO_HIDE = [
  "ASSIGN_PROVIDER",
  "ENABLE_ORDER",
  "DISPATCH_TO_PROVIDER",
  "PROVIDER_PUBLISHED_ORDER_COMPLETED_REPORT",
  "PROVIDER_PUBLISHED_METRICS_REPORT",
  "PROVIDER_PUBLISHED_FLEET_REPORT",
  "PROVIDER_UPDATED_STATE",
  "DRIVER_COMPLETED",
  "TRANSACTION"
]

const EventTimeDiv = styled.div`
  font-size: 13px;
  font-weight: 600;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  color: #989898;
`

const EventTitle = styled.div`
  color: #000000;
`

const EventDetailDiv = styled.div`
  font-size: 13px;
  font-weight: 600;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  color: ${p => (p.color ? p.color : "#989898")};
`
const EventWarning = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #f77c00;
`

const EventHeader = styled.div`
  display: flex;
  width: 100%;
  flex-flow: row wrap;
  align-items: flex-start;
`

const EventHeaderTitle = styled.div`
  font-size: 18px;
  font-weight: 800;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  color: #000000;
  margin-bottom: 20px;
`

const ShowMoreButton = styled.div`
  margin-left: auto;
`

const OrderEventPanel = props => {
  const {
    intl: { formatMessage }
  } = props

  const { detailedOrder } = OrderSearchStore

  const [showInternalEvents, setShowInternalEvents] = useState(false)

  const onShowMoreClicked = () => {
    setShowInternalEvents(!showInternalEvents)
  }

  const eventFilter = (event: OrderEvent) => {
    // hide non relevant events
    // show PROVIDER_PUBLISHED_FLEET_REPORT if it contains fleet name
    return (
      !includes(EVENTS_TO_HIDE, event.type) ||
      !isUndefined(event.fleetName) ||
      (event.type === "DISPATCH_TO_PROVIDER" && event.success === false)
    )
  }

  const renderCarAssignedETA = (travelNow: boolean, item: OrderEvent) => {
    return (
      <EventDetailDiv
        color={
          travelNow && item.etaCarArrival.timeMinutes > 5
            ? "#f77c00"
            : !travelNow &&
              ((item.pickupTimeDifference.type === "BEFORE" &&
                item.pickupTimeDifference.timeMinutes <
                  item.etaCarArrival.timeMinutes) ||
                item.pickupTimeDifference.type !== "BEFORE")
            ? "#BF0000"
            : null
        }
      >
        {travelNow ? (
          <Tooltip
            placement="bottomLeft"
            title={
              item.etaCarArrival.timeMinutes > 5
                ? formatMessage({
                    id: "search.events.driveraccepted.eta.message.tooltip"
                  })
                : ""
            }
          >
            {formatMessage(
              { id: "apps.order-search.events.driveraccepted.eta.message" },
              { minutes: item.etaCarArrival.timeMinutes }
            )}
          </Tooltip>
        ) : (
          <>
            {formatMessage(
              {
                id:
                  item.pickupTimeDifference.type === "BEFORE"
                    ? "apps.order-search.events.driveraccepted.eta.message.prebooking"
                    : item.pickupTimeDifference.type === "AFTER"
                    ? "apps.order-search.events.driveraccepted.eta.message.prebooking.afterdeparture"
                    : "apps.order-search.events.driveraccepted.eta.message.prebooking.atdeparture"
              },
              {
                minutes: item.pickupTimeDifference.timeMinutes,
                minutesAway: item.etaCarArrival.timeMinutes
              }
            )}
          </>
        )}
      </EventDetailDiv>
    )
  }

  const renderCustomerInCarETA = (travelNow: boolean, eta: number) => {
    return (
      <EventDetailDiv color={eta > 5 ? "#BF0000" : null}>
        {formatMessage(
          {
            id: travelNow
              ? "apps.order-search.events.invehicle.late.message.directbooking"
              : "apps.order-search.events.invehicle.late.message.prebooking"
          },
          { minutes: eta }
        )}
      </EventDetailDiv>
    )
  }

  const renderEventTimeline = () => {
    if (
      !isUndefined(detailedOrder) &&
      !isUndefined(detailedOrder.orderEvents)
    ) {
      const eventsToRender = showInternalEvents
        ? detailedOrder.orderEvents
        : filter(detailedOrder.orderEvents, e => eventFilter(e))

      const travelNow = detailedOrder.travelNow
      const labelKey = showInternalEvents
        ? "apps.order-search.label.hideInternalEvents"
        : "apps.order-search.label.showInternalEvents"
      const resendInviteAction = [
        {
          key: "edit",
          type: "edit",
          name: formatMessage({
            id: labelKey
          }),
          onClick: onShowMoreClicked
        }
      ]
      return (
        <div>
          <EventHeader>
            <EventHeaderTitle>
              {formatMessage({ id: "apps.order-search.title.events" })}
            </EventHeaderTitle>
            <ShowMoreButton>
              <DotsDropdown actions={[...resendInviteAction]} />
            </ShowMoreButton>
          </EventHeader>
          <Timeline>
            {eventsToRender.map((item, index) => (
              <Timeline.Item key={index}>
                <EventTitle>
                  {formatMessage({
                    id:
                      "apps.order-search.events." +
                      lowerCase(replace(item.type, /_/g, ""))
                  })}
                </EventTitle>
                <EventDetailDiv>
                  {item.noShowType &&
                    formatMessage({
                      id:
                        "apps.order-search.events.noshowtype." +
                        lowerCase(replace(item.noShowType, /_/g, ""))
                    })}
                </EventDetailDiv>
                <EventDetailDiv color={"#BF0000"}>
                  {item.success === false &&
                    item.type === "CREATE_ORDER" &&
                    formatMessage({
                      id: "apps.order-search.state.failed"
                    })}
                </EventDetailDiv>
                <EventDetailDiv>
                  {item.brand &&
                    item.client &&
                    formatMessage({ id: "apps.order-search.label.via" }) +
                      (!isUndefined(item.appVersion)
                        ? " " + item.appVersion
                        : "") +
                      " " +
                      toTitleCase(item.brand) +
                      " " +
                      item.client}
                </EventDetailDiv>
                {item.skipCarQuotaCheck && (
                  <EventWarning>
                    <Tooltip
                      placement="bottomLeft"
                      title={formatMessage({
                        id:
                          "apps.order-search.events.skipcarquota.warning.tooltip"
                      })}
                    >
                      {item.nextPickupTime
                        ? formatMessage(
                            {
                              id:
                                "apps.order-search.events.skipcarquota.withtime.warning"
                            },
                            {
                              nextPickupTime: moment(
                                item.nextPickupTime
                              ).format("DD MMMM, HH:mm")
                            }
                          )
                        : formatMessage({
                            id:
                              "apps.order-search.events.skipcarquota.notime.warning"
                          })}
                    </Tooltip>
                  </EventWarning>
                )}
                <EventDetailDiv>
                  {item.type === "CANCEL_ORDER" &&
                    !isUndefined(item.sourceSystem) &&
                    formatMessage({ id: "apps.order-search.label.via" }) +
                      " " +
                      item.sourceSystem}
                </EventDetailDiv>
                <EventDetailDiv>
                  {!isUndefined(item.bookingAgent) &&
                    formatMessage({ id: "common.by" }) +
                      " " +
                      item.bookingAgent}
                  {!isUndefined(item.vehicleRegNo) &&
                    formatMessage({ id: "apps.order-search.label.car" }) +
                      " " +
                      item.vehicleRegNo +
                      " "}
                  {!isUndefined(item.driverLicenseId) &&
                    formatMessage({ id: "apps.order-search.label.driver" }) +
                      " " +
                      item.driverLicenseId}
                  {!isUndefined(item.fleetName) && item.fleetName}
                  {!isUndefined(item.handledUser) &&
                    formatMessage({ id: "common.by" }) + " " + item.handledUser}
                </EventDetailDiv>
                <EventDetailDiv>
                  {item.cancellationReason &&
                    `${formatMessage({
                      id:
                        "apps.users.travelers.traveler.vouchers.giveVoucher.reason"
                    })}: "${item.cancellationReason.label}"`}
                </EventDetailDiv>
                {item.pickupTimeDifference &&
                  item.pickupTimeDifference.type === "AFTER" &&
                  item.type === "IN_VEHICLE" &&
                  renderCustomerInCarETA(
                    travelNow,
                    item.pickupTimeDifference.timeMinutes
                  )}
                {item.etaDifference &&
                  item.etaDifference.type === "AFTER" &&
                  renderCustomerInCarETA(
                    travelNow,
                    item.etaDifference.timeMinutes
                  )}
                {item.etaCarArrival &&
                  item.type === "PROVIDER_PUBLISHED_DISPATCH_REPORT" &&
                  renderCarAssignedETA(travelNow, item)}
                {item.etaCarArrival && item.type === "CREATE_ORDER" && (
                  <EventDetailDiv>
                    {formatMessage(
                      {
                        id: "apps.order-search.events.createorder.eta.message"
                      },
                      { minutes: item.etaCarArrival.timeMinutes }
                    )}
                  </EventDetailDiv>
                )}
                <EventTimeDiv>
                  {moment(item.receivedAt).format("YYYY-MM-DD HH:mm:ss")}
                </EventTimeDiv>
              </Timeline.Item>
            ))}
          </Timeline>
        </div>
      )
    } else {
      return <WideSpinner />
    }
  }

  return <div>{renderEventTimeline()}</div>
}

export default (injectIntl(observer(OrderEventPanel)): any)
